// @ts-check
import { Button, Flex, Link } from '~/design-system';
import { Trans } from '@lingui/macro';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useDeleteImport from '../ImportsOverview/useDeleteImport';
import CancelImportDialog from './CancelImportDialog';

export default function Footer({
  cancel = {},
  back = {},
  next = {},
  children,
}) {
  return (
    <Flex
      gap={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        width: '100%',
        paddingX: 4,
        paddingY: 3,
        backgroundColor: 'nuanced',
        alignItems: 'center',
      }}
    >
      {children || (
        // Simulates the old behavior.
        <React.Fragment>
          {(back.onClick || back.url) && (
            <BackButton to={back.url} onClick={back.onClick} />
          )}
          {(cancel.onClick || cancel.url) && (
            <CancelButton to={cancel.url} onClick={cancel.onClick} />
          )}
          {(next.onClick || next.url) && (
            <NextButton
              to={next.url}
              onClick={next.onClick}
              disabled={next.disabled}
            >
              {next.text}
            </NextButton>
          )}
        </React.Fragment>
      )}
    </Flex>
  );
}

/**
 *
 * @param {object} props
 * @param {string} [props.to]
 * @param {() => void} [props.onClick]
 * @param {boolean} [props.disabled]
 * @param {React.ReactNode} [props.children] A custom text for the button
 * @returns
 */
export function NextButton({ to, onClick, children, disabled, ...rest }) {
  if (disabled)
    return (
      <Button variant="primary" sx={{ marginLeft: 'auto' }} disabled {...rest}>
        {children || <Trans>Next</Trans>}
      </Button>
    );
  return (
    <Button
      variant="primary"
      to={to}
      onClick={onClick}
      sx={{ marginLeft: 'auto' }}
      {...rest}
    >
      {children || <Trans>Next</Trans>}
    </Button>
  );
}

/**
 *
 * @param {object} props
 * @param {string} [props.to]
 * @param {() => void} [props.onClick]
 * @param {boolean} [props.disabled]
 * @returns
 */
export function BackButton({ to, onClick, ...rest }) {
  return (
    <Button onClick={onClick} to={to} variant="secondary" {...rest}>
      <Trans>Back</Trans>
    </Button>
  );
}

/**
 *
 * @param {object} props
 * @param {import('react').ReactNode} [props.children]
 * @param {string} [props.to]
 * @param {() => void} [props.onClick]
 * @returns
 */
export function CancelButton({ children, to, onClick, ...rest }) {
  const history = useHistory();
  /** @type {{ importId?: string }} */
  const { importId } = useParams();
  const [open, setOpen] = React.useState(false);
  const { deleteImport } = useDeleteImport({ refetch: async () => {} });

  const label = children || <Trans>Cancel import</Trans>;

  // No import exists yet (e.g. the uploadFile step before creation): there's
  // nothing to delete, so preserve the old behavior of straight navigation.
  if (!importId) {
    return (
      <Link to={to} onClick={onClick} sx={{ display: 'block' }} {...rest}>
        {label}
      </Link>
    );
  }

  return (
    <>
      <Link
        onClick={() => {
          onClick?.();
          setOpen(true);
        }}
        sx={{ display: 'block' }}
        {...rest}
      >
        {label}
      </Link>
      {open && (
        <CancelImportDialog
          onCancelImport={async () => {
            const success = await deleteImport(importId);
            if (!success) return; // leave the modal open so the user can retry
            setOpen(false);
            if (to) history.push(to);
          }}
          onSaveForLater={() => {
            setOpen(false);
            if (to) history.push(to);
          }}
          onKeepEditing={() => setOpen(false)}
        />
      )}
    </>
  );
}
